import { c as _c } from "react/compiler-runtime";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { isAllowedToChangeManagement } from "@pages/project-details/project-overview/change-management/change-management-utils";
import { HttpUtils } from "@stellar/utils";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { isProjectDemo } from "@utils/project-utils";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
// Context that provides the state of the group members
const GroupMembersContext = createContext(undefined);

/**
 * Provider that manages the state of the members of a specific group
 * based on the provided group ID.
 *
 * It automatically fetches the group members whenever the selected group ID changes,
 * and stores the data accordingly.
 */
export function GroupMembersProvider(t0) {
  const $ = _c(25);
  const {
    project,
    children
  } = t0;
  const companyId = useCompanyId();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const currentUser = useAppSelector(currentUserSelector);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [groupMembers, setGroupMembers] = useState(t1);
  const [selectedGroupId, setSelectedGroupId] = useState(project?.group?.id);
  const [refreshCount, setRefreshCount] = useState(0);
  let t2;
  if ($[1] !== companyId || $[2] !== currentUser?.role || $[3] !== handleErrorWithToast || $[4] !== selectedGroupId) {
    t2 = () => {
      const onGroupSelected = async function onGroupSelected(groupId) {
        if (!groupId) {
          return;
        }
        setGroupMembers([]);
        ;
        try {
          const {
            members
          } = await faroCoreApiClient.groups.getGroupById({
            companyId,
            groupId
          });
          setGroupMembers(members);
        } catch (t3) {
          const error = t3;
          const errorStatus = HttpUtils.getErrorStatus(error);
          if (errorStatus && errorStatus === HttpUtils.HTTP_ERROR_CODE.Forbidden) {
            setGroupMembers([]);
          } else {
            handleErrorWithToast({
              id: `getGroupById-${Date.now().toString()}`,
              title: "Cannot get the group details",
              error
            });
          }
        }
      };
      const isRestrictedRole = currentUser?.role === "PROJECT_MANAGER" || currentUser?.role === "MEMBER";
      if (!isRestrictedRole) {
        onGroupSelected(selectedGroupId);
      }
    };
    $[1] = companyId;
    $[2] = currentUser?.role;
    $[3] = handleErrorWithToast;
    $[4] = selectedGroupId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== companyId || $[7] !== currentUser || $[8] !== handleErrorWithToast || $[9] !== refreshCount || $[10] !== selectedGroupId) {
    t3 = [companyId, selectedGroupId, handleErrorWithToast, currentUser, refreshCount];
    $[6] = companyId;
    $[7] = currentUser;
    $[8] = handleErrorWithToast;
    $[9] = refreshCount;
    $[10] = selectedGroupId;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  useEffect(t2, t3);
  let t4;
  if ($[12] !== currentUser || $[13] !== groupMembers || $[14] !== project) {
    bb0: {
      if (!project || !currentUser) {
        t4 = false;
        break bb0;
      }
      const isDemo = isProjectDemo(project);
      if (!project.group) {
        if (!isDemo && currentUser.role === "COMPANY_EXECUTIVE") {
          t4 = true;
          break bb0;
        }
        t4 = isAllowedToChangeManagement(isDemo, project);
        break bb0;
      }
      if (groupMembers.length === 0) {
        t4 = false;
        break bb0;
      }
      if (!isDemo && currentUser.role === "COMPANY_EXECUTIVE") {
        t4 = true;
        break bb0;
      }
      const projectManager = project.managers.projectManager;
      if (!projectManager) {
        t4 = false;
        break bb0;
      }
      const currentUserMember = groupMembers.find(member => member.identity === currentUser.identity);
      if (currentUserMember?.role === "PROJECT_MANAGER" || currentUserMember?.role === "GROUP_VIEWER") {
        t4 = false;
        break bb0;
      }
      t4 = isAllowedToChangeManagement(isDemo, project);
    }
    $[12] = currentUser;
    $[13] = groupMembers;
    $[14] = project;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const canChangeManagement = t4;
  let t5;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setRefreshCount(_temp);
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== canChangeManagement || $[18] !== groupMembers || $[19] !== project || $[20] !== selectedGroupId) {
    t6 = {
      project,
      groupMembers,
      setSelectedGroupId,
      selectedGroupId,
      canChangeManagement,
      refreshGroupMembers: t5
    };
    $[17] = canChangeManagement;
    $[18] = groupMembers;
    $[19] = project;
    $[20] = selectedGroupId;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== children || $[23] !== t6) {
    t7 = <GroupMembersContext.Provider value={t6}>{children}</GroupMembersContext.Provider>;
    $[22] = children;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}

/**
 * Hook to consume the group members context.
 * Throws an error if used outside of the GroupMembersProvider context.
 */
function _temp(count) {
  return count + 1;
}
export function useGroupMembersContext() {
  const context = useContext(GroupMembersContext);
  if (!context) {
    throw new Error("useGroupMembersContext must be used within a GroupMembersProvider");
  }
  return context;
}